import { ReactNode, RefObject } from "react"
import { MuxVideo, Recording } from "@/schema"
import MuxVideoPlayer from "@ui/mux-player/mux-video-player"
import VideoLoading from "@ui/mux-player/video-loading"
import VideoUnavailable from "@ui/mux-player/video-unavailable"
import NativeVideoPlayer from "@ui/native-video-player"

import { useMeetingAudioDownloadUrl, useMeetingMuxVideoToken } from "@/hooks/api/meetings"

import RecordingAudioPlayer from "./recording-audio-player"

const audioContainerClassName = "h-auto shrink-0 overflow-hidden p-3 pb-2"
const videoContainerClassName = "relative mx-auto h-auto w-full max-w-[56rem] shrink-0 overflow-hidden p-3 pb-2"

export default function VideoPlayer({
  muxVideo,
  recording,
  meetingId,
  isLoading,
  onTimeUpdate,
  videoRef,
  isVideoCollapsed,
  downloadControl,
  visibilityControl,
}: {
  muxVideo?: MuxVideo
  recording?: Recording
  meetingId?: string
  isLoading: boolean
  onTimeUpdate: (event: Event) => void
  videoRef: RefObject<HTMLVideoElement | HTMLAudioElement | null>
  isVideoCollapsed: boolean
  downloadControl?: ReactNode
  visibilityControl?: ReactNode
}) {
  // Determine which mux_video to use: meeting's notetaker mux_video or recording's mux_video
  const effectiveMuxVideo = muxVideo ?? recording?.mux_video
  const isMuxVideoReady = effectiveMuxVideo?.asset_status === "ready"

  const {
    data: tokens,
    isLoading: isLoadingTokens,
    isError,
  } = useMeetingMuxVideoToken(meetingId || "", {
    enabled: !!meetingId && !!effectiveMuxVideo && isMuxVideoReady,
    staleTime: 1000 * 60 * 55,
    refetchOnWindowFocus: false,
  })

  const isPublic = effectiveMuxVideo?.playback_policy === "public"
  const canViewMux = !!effectiveMuxVideo?.playback_id || (!isPublic && !!tokens?.video_token)
  const hasRecordingMedia = !!recording?.media?.url
  const hasOverlayControls = Boolean(downloadControl || visibilityControl)
  const isVideoMedia = Boolean(effectiveMuxVideo) || recording?.media?.media_type === "video"
  const recordingVideoAudioSrc = recording?.media?.media_type === "video" ? recording.media.url : undefined

  const { data: meetingAudioUrl, isLoading: isLoadingMeetingAudioUrl } = useMeetingAudioDownloadUrl(meetingId || "", {
    enabled: isVideoCollapsed && isVideoMedia && !!meetingId && !recordingVideoAudioSrc,
    staleTime: 0,
    refetchOnWindowFocus: false,
  })

  const collapsedAudioSrc = recordingVideoAudioSrc ?? meetingAudioUrl?.download_url

  const videoOverlay = hasOverlayControls ? (
    <div className="bg-background/60 absolute top-5 right-5 z-10 flex items-center gap-1 rounded-lg p-px opacity-70 backdrop-blur-sm transition-opacity hover:opacity-100">
      {visibilityControl}
      {downloadControl}
    </div>
  ) : null

  // Show loading state
  if (isLoading || (effectiveMuxVideo && isMuxVideoReady && isLoadingTokens)) {
    return (
      <div className={videoContainerClassName}>
        <VideoLoading />
      </div>
    )
  }

  if (isVideoCollapsed && isVideoMedia) {
    if (!collapsedAudioSrc && isLoadingMeetingAudioUrl) {
      return (
        <div className={videoContainerClassName}>
          <VideoLoading />
        </div>
      )
    }

    if (collapsedAudioSrc) {
      const collapsedCurrentTimeSeconds = videoRef.current?.currentTime
      const shouldResumePlayback = Boolean(videoRef.current && !videoRef.current.paused)

      return (
        <div className={audioContainerClassName}>
          <RecordingAudioPlayer
            src={collapsedAudioSrc}
            durationSeconds={recording?.duration_seconds}
            onTimeUpdate={onTimeUpdate}
            ref={videoRef as RefObject<HTMLAudioElement>}
            initialTimeSeconds={collapsedCurrentTimeSeconds}
            autoPlayOnMount={shouldResumePlayback}
            rightControls={
              <>
                {visibilityControl}
                {downloadControl}
              </>
            }
          />
        </div>
      )
    }
  }

  // Case 1: Ready Mux video exists (from notetaker or recording) - use MuxPlayer
  if (effectiveMuxVideo && isMuxVideoReady) {
    if (!canViewMux || isError)
      return (
        <div className={videoContainerClassName}>
          <VideoUnavailable />
        </div>
      )

    return (
      <div className={videoContainerClassName}>
        <MuxVideoPlayer
          muxVideo={effectiveMuxVideo}
          tokens={tokens}
          isPublic={isPublic}
          onTimeUpdate={onTimeUpdate}
          videoRef={videoRef as RefObject<HTMLVideoElement>}
        />
        {videoOverlay}
      </div>
    )
  }

  // Case 2: Mux video is still processing - show loading
  if (effectiveMuxVideo && effectiveMuxVideo.asset_status === "preparing") {
    return (
      <div className={videoContainerClassName}>
        <VideoLoading />
      </div>
    )
  }

  // Case 3 & 4: User-uploaded recording with direct media (fallback while Mux processes or if Mux errored)
  if (hasRecordingMedia && recording?.media) {
    const { url, media_type } = recording.media

    // Case 3: Video recording - use native video player
    if (media_type === "video") {
      return (
        <div className={videoContainerClassName}>
          <NativeVideoPlayer src={url} onTimeUpdate={onTimeUpdate} ref={videoRef as RefObject<HTMLVideoElement>} />
          {videoOverlay}
        </div>
      )
    }

    // Case 4: Audio recording - use audio player
    if (media_type === "audio") {
      return (
        <div className={audioContainerClassName}>
          <RecordingAudioPlayer
            src={url}
            durationSeconds={recording.duration_seconds}
            onTimeUpdate={onTimeUpdate}
            ref={videoRef as RefObject<HTMLAudioElement>}
            rightControls={downloadControl}
          />
        </div>
      )
    }
  }

  // No playable media available
  return (
    <div className={videoContainerClassName}>
      <VideoUnavailable />
    </div>
  )
}
